/* eslint-disable @typescript-eslint/no-explicit-any */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import RNMarkdown, { hasParents } from "@ronradtke/react-native-markdown-display";
import merge from "lodash/merge";
import { memo, useMemo } from "react";
import { Image, Platform, View } from "react-native";
import { Text } from "./Text";
import { useTheme } from "../styles";
// Images https://ouitherapeutics.slack.com/archives/CNRUWC7RC/p1570120768018000
/**
 * Renders content following the [CommonMark](https://commonmark.org/)
 * specification of Markdown.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-42}
 */ export var Markdown = /*#__PURE__*/ memo(function(props) {
    var theme = useTheme().theme;
    var style = useMemo(function() {
        var defaultStyle = {
            body: {
                fontSize: theme.typography.body.size,
                lineHeight: theme.typography.body.lineHeight,
                width: '100%',
                fontFamily: props.fontFamily || 'OpenSansRegular',
                color: props.textColor
            },
            heading1: {
                paddingVertical: 10,
                fontSize: 32,
                lineHeight: 32,
                fontFamily: 'OpenSansBold'
            },
            heading2: {
                paddingVertical: 10,
                fontSize: 24,
                lineHeight: 24,
                fontFamily: 'OpenSansBold'
            },
            heading3: {
                paddingVertical: 10,
                fontSize: 18,
                lineHeight: 18,
                fontFamily: 'OpenSansBold'
            },
            heading4: {
                paddingVertical: 10,
                fontSize: 16,
                lineHeight: 16,
                fontFamily: 'OpenSansBold'
            },
            heading5: {
                paddingVertical: 10,
                fontSize: 13,
                lineHeight: 13,
                fontFamily: 'OpenSansBold'
            },
            heading6: {
                paddingVertical: 10,
                fontSize: 11,
                lineHeight: 11,
                fontFamily: 'OpenSansBold'
            },
            strong: {
                fontSize: 16,
                fontFamily: 'OpenSansBold',
                color: props.textColor
            },
            bullet_list_icon: {
                marginTop: (theme.typography.body.lineHeight - 5) / 2,
                backgroundColor: props.textColor || 'black',
                borderRadius: 5,
                height: 5,
                marginRight: 10,
                width: 5
            },
            list_item: {
                flexShrink: 1,
                flexDirection: 'row',
                justifyContent: 'flex-start'
            }
        };
        return merge({}, defaultStyle, props.style);
    }, [
        props.textColor,
        props.fontFamily,
        props.style,
        theme
    ]);
    return /*#__PURE__*/ _jsx(RNMarkdown, {
        style: style,
        rules: _object_spread({
            image: function(node, children, parent, styles) {
                var content = null;
                try {
                    if (node.content && node.content.startsWith('{')) {
                        content = JSON.parse(node.content);
                    }
                } catch (e) {}
                return /*#__PURE__*/ _jsx(Image, {
                    style: [
                        styles.image,
                        {
                            flex: 0,
                            flexGrow: 1,
                            resizeMode: 'contain',
                            minHeight: 50,
                            alignSelf: 'flex-start'
                        },
                        content
                    ],
                    source: {
                        uri: node.attributes.src
                    }
                }, node.key);
            },
            paragraph: function(node, children, parent, styles) {
                var _anyParent_children_, _anyParent_children_1;
                var anyParent = parent === null || parent === void 0 ? void 0 : parent[0];
                var parentIsRoot = anyParent.type === 'body';
                var isFirstRootChild = parentIsRoot && node.key === ((_anyParent_children_ = anyParent.children[0]) === null || _anyParent_children_ === void 0 ? void 0 : _anyParent_children_.key);
                var isLastRootChild = parentIsRoot && node.key === ((_anyParent_children_1 = anyParent.children[anyParent.children.length - 1]) === null || _anyParent_children_1 === void 0 ? void 0 : _anyParent_children_1.key);
                var isInListItem = hasParents(parent, 'list_item');
                var hasImageChild = !!node.children.find(function(child) {
                    return child.sourceType === 'image';
                });
                return /*#__PURE__*/ _jsx(View, {
                    style: [
                        styles.paragraph,
                        isFirstRootChild ? {
                            marginTop: 0
                        } : 0,
                        isLastRootChild ? {
                            marginBottom: 0
                        } : 0,
                        isInListItem ? {
                            marginTop: 0,
                            marginBottom: 5
                        } : null,
                        hasImageChild ? {
                            alignItems: 'center'
                        } : null
                    ],
                    children: children
                }, node.key);
            },
            list_item: function(node, children, parent, styles) {
                var inheritedStyles = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : {};
                if (hasParents(parent, 'bullet_list')) {
                    return /*#__PURE__*/ _jsxs(View, {
                        style: styles.list_item,
                        children: [
                            /*#__PURE__*/ _jsx(View, {
                                style: styles.bullet_list_icon
                            }),
                            /*#__PURE__*/ _jsx(View, {
                                style: styles.bullet_list_content,
                                children: children
                            })
                        ]
                    }, node.key);
                }
                if (hasParents(parent, 'ordered_list')) {
                    var depth = parent.filter(function(p) {
                        return p.type === 'ordered_list';
                    }).length;
                    var isAlphaList = depth % 2 === 0;
                    var orderedListIndex = parent.findIndex(function(el) {
                        return el.type === 'ordered_list';
                    });
                    var orderedList = parent[orderedListIndex];
                    var listItemNumber;
                    if (orderedList.attributes && orderedList.attributes.start) {
                        listItemNumber = orderedList.attributes.start + node.index;
                    } else {
                        listItemNumber = node.index + 1;
                    }
                    return /*#__PURE__*/ _jsxs(View, {
                        style: [
                            styles.list_item
                        ],
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                style: [
                                    styles.ordered_list_icon,
                                    Platform.OS === 'android' ? {
                                        lineHeight: 18
                                    } : undefined,
                                    {
                                        alignSelf: 'flex-start',
                                        fontFamily: props.fontFamily
                                    }
                                ],
                                color: props.textColor,
                                text: isAlphaList ? "".concat(String.fromCharCode('a'.charCodeAt(0) + node.index)).concat(node.markup, " ") : "".concat(listItemNumber).concat(node.markup, " ")
                            }),
                            /*#__PURE__*/ _jsx(View, {
                                style: styles.ordered_list_content,
                                children: children
                            })
                        ]
                    }, node.key);
                }
                return /*#__PURE__*/ _jsx(View, {
                    style: styles.list_item,
                    children: children
                }, node.key);
            }
        }, props.rules),
        children: props.children
    });
});
